// ========================================
// Critical Stock PDF Document
// ========================================
import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    Image,
} from '@react-pdf/renderer';

// רישום פונט עברי (אופציונלי - נשתמש בפונט רגיל לעת עתה)
// Font.register({ family: 'Heebo', src: '...' });

// סגנונות ה-PDF
const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        fontFamily: 'Helvetica',
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 30,
        paddingBottom: 15,
        borderBottomWidth: 2,
        borderBottomColor: '#E30613',
    },
    logo: {
        width: 80,
        height: 40,
    },
    headerText: {
        textAlign: 'right',
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#1a1a1a',
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 10,
        color: '#666666',
    },
    summarySection: {
        backgroundColor: '#f8f8f8',
        padding: 15,
        borderRadius: 4,
        marginBottom: 20,
    },
    summaryTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#333333',
        marginBottom: 10,
    },
    summaryText: {
        fontSize: 10,
        color: '#555555',
        lineHeight: 1.5,
    },
    statsRow: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginTop: 15,
        paddingTop: 15,
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
    },
    statBox: {
        alignItems: 'center',
    },
    statValue: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#E30613',
    },
    statLabel: {
        fontSize: 8,
        color: '#888888',
        marginTop: 2,
    },
    tableSection: {
        marginTop: 20,
    },
    tableTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#333333',
        marginBottom: 10,
    },
    table: {
        display: 'table',
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    tableRowHeader: {
        flexDirection: 'row',
        backgroundColor: '#1a1a1a',
    },
    tableRowAlt: {
        backgroundColor: '#f9f9f9',
    },
    tableCell: {
        padding: 8,
        fontSize: 9,
        textAlign: 'center',
    },
    tableCellHeader: {
        padding: 8,
        fontSize: 9,
        fontWeight: 'bold',
        color: '#ffffff',
        textAlign: 'center',
    },
    colSku: { width: '20%' },
    colName: { width: '35%', textAlign: 'left' },
    colStock: { width: '15%' },
    colOnOrder: { width: '15%' },
    colStatus: { width: '15%' },
    statusRed: {
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        padding: 4,
        borderRadius: 2,
        fontSize: 8,
        fontWeight: 'bold',
    },
    statusYellow: {
        backgroundColor: '#fef3c7',
        color: '#d97706',
        padding: 4,
        borderRadius: 2,
        fontSize: 8,
        fontWeight: 'bold',
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: 8,
        color: '#999999',
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
        paddingTop: 10,
    },
    pageNumber: {
        textAlign: 'center',
    },
});

/**
 * PDF דוח מלאי קריטי
 * @param {Array} items - רשימת הפריטים הקריטיים
 * @param {string} generatedDate - תאריך הפקת הדוח
 */
const CriticalStockDocument = ({ items = [], generatedDate }) => {
    const date = generatedDate || new Date().toLocaleDateString('he-IL');

    // חישוב סטטיסטיקות
    const totalItems = items.length;
    const outOfStock = items.filter(i => (i.PhysicalStock || 0) <= 0).length;
    const lowStock = totalItems - outOfStock;
    const totalMissingUnits = items.reduce((sum, i) => {
        const stock = i.PhysicalStock || 0;
        return sum + (stock < 0 ? Math.abs(stock) : 0);
    }, 0);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerText}>
                        <Text style={styles.title}>CRITICAL STOCK REPORT</Text>
                        <Text style={styles.subtitle}>AEG Inventory Intelligence | {date}</Text>
                    </View>
                    <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#E30613' }}>AEG</Text>
                </View>

                {/* Executive Summary */}
                <View style={styles.summarySection}>
                    <Text style={styles.summaryTitle}>Executive Summary</Text>
                    <Text style={styles.summaryText}>
                        This report identifies {totalItems} items requiring immediate attention.
                        {outOfStock > 0 && ` ${outOfStock} items are completely out of stock.`}
                        {lowStock > 0 && ` ${lowStock} items are running critically low.`}
                        {' '}Urgent action is recommended to prevent stockouts and lost sales.
                    </Text>

                    <View style={styles.statsRow}>
                        <View style={styles.statBox}>
                            <Text style={styles.statValue}>{totalItems}</Text>
                            <Text style={styles.statLabel}>CRITICAL ITEMS</Text>
                        </View>
                        <View style={styles.statBox}>
                            <Text style={styles.statValue}>{outOfStock}</Text>
                            <Text style={styles.statLabel}>OUT OF STOCK</Text>
                        </View>
                        <View style={styles.statBox}>
                            <Text style={styles.statValue}>{lowStock}</Text>
                            <Text style={styles.statLabel}>LOW STOCK</Text>
                        </View>
                        <View style={styles.statBox}>
                            <Text style={styles.statValue}>{totalMissingUnits}</Text>
                            <Text style={styles.statLabel}>BACKORDER UNITS</Text>
                        </View>
                    </View>
                </View>

                {/* Items Table */}
                <View style={styles.tableSection}>
                    <Text style={styles.tableTitle}>Critical Items Detail</Text>

                    <View style={styles.table}>
                        {/* Header Row */}
                        <View style={styles.tableRowHeader}>
                            <Text style={[styles.tableCellHeader, styles.colSku]}>SKU</Text>
                            <Text style={[styles.tableCellHeader, styles.colName]}>Description</Text>
                            <Text style={[styles.tableCellHeader, styles.colStock]}>Stock</Text>
                            <Text style={[styles.tableCellHeader, styles.colOnOrder]}>On Order</Text>
                            <Text style={[styles.tableCellHeader, styles.colStatus]}>Status</Text>
                        </View>

                        {/* Data Rows */}
                        {items.slice(0, 25).map((item, index) => {
                            const stock = item.PhysicalStock || 0;
                            const isOutOfStock = stock <= 0;

                            return (
                                <View
                                    key={item.SKU}
                                    style={[
                                        styles.tableRow,
                                        index % 2 === 1 && styles.tableRowAlt
                                    ]}
                                >
                                    <Text style={[styles.tableCell, styles.colSku]}>
                                        {item.SKU}
                                    </Text>
                                    <Text style={[styles.tableCell, styles.colName]} numberOfLines={1}>
                                        {(item.Description || '').substring(0, 40)}
                                    </Text>
                                    <Text style={[styles.tableCell, styles.colStock, { color: isOutOfStock ? '#dc2626' : '#d97706', fontWeight: 'bold' }]}>
                                        {stock}
                                    </Text>
                                    <Text style={[styles.tableCell, styles.colOnOrder]}>
                                        {item.OnOrder || 0}
                                    </Text>
                                    <View style={[styles.tableCell, styles.colStatus]}>
                                        <Text style={isOutOfStock ? styles.statusRed : styles.statusYellow}>
                                            {isOutOfStock ? 'OUT' : 'LOW'}
                                        </Text>
                                    </View>
                                </View>
                            );
                        })}
                    </View>

                    {items.length > 25 && (
                        <Text style={{ marginTop: 10, fontSize: 8, color: '#999' }}>
                            + {items.length - 25} more items not shown
                        </Text>
                    )}
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text>AEG Inventory Intelligence System</Text>
                    <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
                    <Text>Generated: {date}</Text>
                </View>
            </Page>
        </Document>
    );
};

export default CriticalStockDocument;
